import type { Locale } from '../i18n'

export type GuideDoc = {
  title: string
  description: string
  intro: string
  sections: { heading: string; body: string[] }[]
}

/** 解説は日本語と英語だけを正本として持つ。ほかの言語には英語版を出す */
type GuideLocale = 'ja' | 'en'

const pomodoro: Record<GuideLocale, GuideDoc> = {
  ja: {
    title: 'ポモドーロ・テクニックとは',
    description:
      '25分の集中と5分の休憩をくり返す時間管理の方法、ポモドーロ・テクニックのやり方と続けるコツ、Focus Cafe での使い方をまとめました。',
    intro:
      'ポモドーロ・テクニックは、作業を25分の集中と5分の休憩に区切ってくり返す時間管理の方法です。決まった時間だけ一つの作業に向き合い、時間が来たら手を止めて休みます。タイマーが一つあれば、今日からはじめられます。',
    sections: [
      {
        heading: '名前の由来',
        body: [
          '1980年代の終わりに、イタリアの大学生だったフランチェスコ・シリロが、勉強に集中するために考えた方法です。そのとき使っていたトマトの形のキッチンタイマーにちなんで、イタリア語でトマトを意味する「ポモドーロ」と名づけられました。',
          '25分の集中と5分の休憩をあわせた一区切りも「1ポモドーロ」と数えます。',
        ],
      },
      {
        heading: '基本のやり方',
        body: [
          '1. 取り組む作業を一つ決めます。',
          '2. タイマーを25分に合わせて、鳴るまでその作業だけをします。',
          '3. タイマーが鳴ったら手を止め、5分休みます。席を立って体を動かしたり、水を飲んだりして、作業から頭を離します。',
          '4. これを4回くり返したら、15〜30分の長めの休憩を取ります。',
        ],
      },
      {
        heading: 'なぜ集中しやすくなるのか',
        body: [
          '終わりの時間が見えていると、取りかかるときの腰の重さが軽くなります。「とりあえず25分だけ」と思えば、大きな作業にも手をつけられます。',
          '疲れきる前に休むので、一日の後半まで集中が持ちます。休憩を後回しにして続けるより、短く区切ったほうが合計の作業時間が長くなることも多いです。',
          'こなしたポモドーロの数を数えておくと、一つの作業にどれだけ時間がかかったかがわかります。次に同じような作業をするとき、見積もりの手がかりになります。',
        ],
      },
      {
        heading: '途中で気が散ったとき',
        body: [
          '集中している最中にほかの用事を思い出したら、紙にひとこと書き留めて、すぐ作業に戻ります。書いておけば忘れる心配がないので、頭から追い出せます。書いたことは休憩中か、そのポモドーロが終わってから片づけます。',
          '人から声をかけられたり、急ぎの連絡が来たりして中断せざるを得ないときは、そのポモドーロはなかったことにして、あらためて25分からはじめ直します。',
        ],
      },
      {
        heading: '続けるためのコツ',
        body: [
          '25分は目安です。長く感じるなら15分から、物足りないなら50分にのばしてもかまいません。自分が最後まで集中を保てる長さに合わせます。',
          '休憩中にスマートフォンや SNS を見ると、頭が作業から離れたまま戻りにくくなります。休憩は目と頭を休める時間にします。',
          '一つのポモドーロで終わらない大きな作業は、25分で終わる大きさに分けます。小さな作業は、いくつかまとめて一つのポモドーロに入れます。',
        ],
      },
      {
        heading: 'Focus Cafe で試す',
        body: [
          'Focus Cafe は、Web で使えるポモドーロタイマーです。店内に入って時間を選び、「集中する」を押すと、自分の席についてタイマーがはじまります。同じ時間に集中しているほかの人たちも、同じ店内の席に座っています。',
          '時間は「25分 集中 / 5分 休憩」のほか、「50分 / 10分」「90分 / 15分」から選べます。「自分で決める」を選ぶと、集中は1〜180分、休憩は1〜60分で決められます。',
          '集中が終わるとレシートを受け取り、その回の作業を記録として残せます。乗っているときは、休憩せずに続けて集中することもできます。登録しなくても、ゲストのまま使えます。',
        ],
      },
    ],
  },
  en: {
    title: 'What is the Pomodoro Technique?',
    description:
      'How the Pomodoro Technique works — 25 minutes of focus followed by a 5-minute break — with tips for sticking with it and using it at Focus Cafe.',
    intro:
      'The Pomodoro Technique is a time management method that splits your work into 25 minutes of focus followed by a 5-minute break, over and over. You give one task your full attention for a fixed stretch, then stop and rest when the time is up. All you need is a timer, so you can start today.',
    sections: [
      {
        heading: 'Where the name comes from',
        body: [
          'Francesco Cirillo came up with the method in the late 1980s, as a university student in Italy trying to focus on his studies. He named it after the tomato-shaped kitchen timer he used: pomodoro is Italian for tomato.',
          'One round of 25 minutes of focus plus a 5-minute break is also called "one pomodoro."',
        ],
      },
      {
        heading: 'How to do it',
        body: [
          '1. Pick one task to work on.',
          '2. Set a timer for 25 minutes and work only on that task until it rings.',
          '3. When the timer rings, stop and take a 5-minute break. Stand up, stretch, drink some water — get your mind off the work.',
          '4. After four rounds, take a longer break of 15 to 30 minutes.',
        ],
      },
      {
        heading: 'Why it helps you focus',
        body: [
          'When you can see the finish line, it is easier to get started. Telling yourself "just 25 minutes" makes even a big task feel approachable.',
          'Because you rest before you are worn out, your focus lasts into the afternoon. Working in short rounds often adds up to more total work than pushing on and skipping breaks.',
          'Counting the pomodoros you complete shows how long a task really took. That gives you something to go on the next time you estimate similar work.',
        ],
      },
      {
        heading: 'When something distracts you',
        body: [
          'If you remember something else you need to do in the middle of a round, jot it down on paper and go straight back to work. Once it is written down you will not forget it, so you can let it go. Deal with it during the break or after the round ends.',
          'If someone interrupts you or an urgent message forces you to stop, that round does not count. Start over from 25 minutes.',
        ],
      },
      {
        heading: 'Tips for sticking with it',
        body: [
          '25 minutes is a starting point. If it feels too long, try 15; if it feels too short, stretch it to 50. Use the length you can stay focused for all the way through.',
          'Checking your phone or social media during a break keeps your mind away from the work and makes it harder to come back. Use the break to rest your eyes and your head.',
          'Split tasks too big for one pomodoro into pieces that fit in 25 minutes. Bundle small tasks together into a single pomodoro.',
        ],
      },
      {
        heading: 'Try it at Focus Cafe',
        body: [
          'Focus Cafe is a Pomodoro timer on the web. Go inside, choose a length, and press Start focusing: you take a seat and the timer starts. Other people focusing at the same time are sitting in the same café.',
          'Besides 25 minutes of focus with a 5-minute break, you can choose 50/10 or 90/15. Pick Custom to choose 1 to 180 minutes of focus and 1 to 60 minutes of rest.',
          'When a round ends you get a receipt, and you can keep it as a record of what you worked on. If you are on a roll, you can keep focusing without a break. You can use it as a guest without signing up.',
        ],
      },
    ],
  },
}

export function pomodoroDoc(locale: Locale): { doc: GuideDoc; lang: GuideLocale } {
  const lang: GuideLocale = locale === 'ja' ? 'ja' : 'en'
  return { doc: pomodoro[lang], lang }
}
